/**
 * # AsInspector Component
 *
 * A collapsible side panel inspector that opens on the right side of the viewport.
 * Contains accordion sections for organizing content.
 *
 * ## Features
 * - Opens/closes via events or toggle button
 * - Arrow button on left edge indicates open/close state
 * - Contains accordion sections for output meshes, projects, and help-info
 *
 * ## Events
 * ### Listened Events
 * - `as-inspector-open`: Opens the inspector panel
 * - `as-inspector-close`: Closes the inspector panel
 *
 * ### Emitted Events
 * - `as-inspector-toggle`: Dispatched when inspector state changes
 *   - Detail: `{ show: boolean }`
 *
 * ## Attributes
 * - `show`: When present, the inspector is visible
 *
 * ## Usage
 * ```html
 * <as-inspector id="inspector"></as-inspector>
 * ```
 *
 * ```javascript
 * // Open the inspector
 * window.dispatchEvent(new CustomEvent('as-inspector-open'));
 *
 * // Close the inspector
 * window.dispatchEvent(new CustomEvent('as-inspector-close'));
 * ```
 */
import '../layout/as-accordion.ts';
import '../layout/as-tabs.ts';
import '../base/buttons/as-icon-button.ts';
import '../layout/as-portal-target.ts';
import '../debug/as-debug-project-view.ts';
import './as-projects-list.ts';
import './as-changelog.ts';
import './as-notification-history.ts';
import './as-embed-content.ts';
import styleText from './as-inspector.css?raw';
import { isProduction, suppressExternalServices } from '../../data/constants.ts';

export class AsInspector extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  static get observedAttributes(): string[] {
    return ['show', 'disabled', 'show-debug'];
  }

  private boundHandleOpen: () => void;
  private boundHandleClose: () => void;
  private boundHandleToggle: () => void;

  private _embedKey: string | null = null; // null until configured
  private _embedReady = false;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsInspector.sheet];

    this.boundHandleOpen = this.handleOpen.bind(this);
    this.boundHandleClose = this.handleClose.bind(this);
    this.boundHandleToggle = this.toggle.bind(this);

    this.render();
  }

  private initEmbedIfNeeded(): void {
    if (this._embedReady || !this._embedKey) return;
    this._embedReady = true;
    this.shadowRoot?.querySelector('as-embed-content')
      ?.setAttribute('embed-module-key', this._embedKey);
  }

  private render(): void {
    if (this.hasAttribute('disabled')) {
      this.shadowRoot!.innerHTML = '';
      return;
    }
    this._embedReady = false;

    this.shadowRoot!.innerHTML = /*html*/`
      <button class="toggle-button" part="toggle">
        <span class="toggle-arrow"></span>
      </button>
      <div class="inspector-panel">
        <div class="inspector-header">
          <span class="inspector-title">Inspector</span>
          <as-icon-button
            class="close-button"
            name="close-inspector"
            file-name="icons/close.svg"
            black
            small
          ></as-icon-button>
        </div>
        <div class="inspector-content">
          <div class="inspector-scroll-area">
            <!-- Projects accordion disabled — IDB project management removed for now.
            <as-accordion header="Projects" open help-tooltip-default-text="Projects are save files for a specific app-suite app. They store your output meshes and parameter settings.">
              <slot name="projects">
                <as-projects-list></as-projects-list>
              </slot>
            </as-accordion>
            -->
            ${this.hasAttribute('show-debug') ? `<as-accordion header="Debug" open>
                <as-debug-project-view></as-debug-project-view>
                <as-portal-target name="debug"></as-portal-target>
              </as-accordion>` : ''}
          </div>
          <as-tabs accordion>
            <div tab-label="Welcome">
              <as-embed-content></as-embed-content>
            </div>
            ${!suppressExternalServices ? `
            <div tab-label="Changelog">
              <as-changelog src="${isProduction ? 'data/changelog-production.json' : 'data/changelog-production.json,data/changelog-dev.json'}"></as-changelog>
            </div>
            ` : ''}
            <div tab-label="Notifications">
              <as-notification-history></as-notification-history>
            </div>
          </as-tabs>
        </div>
      </div>
    `;

    // Set up button listeners
    this.shadowRoot?.querySelector('.toggle-button')?.addEventListener('click', () => this.toggle());
    this.shadowRoot?.querySelector('.close-button')?.addEventListener('click', () => this.handleClose());
  }

  connectedCallback(): void {
    window.addEventListener('as-inspector-open', this.boundHandleOpen);
    window.addEventListener('as-inspector-close', this.boundHandleClose);
    window.addEventListener('as-inspector-toggle', this.boundHandleToggle);
  }

  disconnectedCallback(): void {
    window.removeEventListener('as-inspector-open', this.boundHandleOpen);
    window.removeEventListener('as-inspector-close', this.boundHandleClose);
    window.removeEventListener('as-inspector-toggle', this.boundHandleToggle);
  }

  private handleOpen(): void {
    if (this.hasAttribute('disabled')) return;
    this.show = true;
  }

  private handleClose(): void {
    this.show = false;
  }

  public toggle(): void {
    if (this.hasAttribute('disabled')) return;
    this.show = !this.show;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'disabled' && oldValue !== newValue) {
      if (newValue !== null) {
        // Just got disabled - clear content and close
        this.show = false;
        this.shadowRoot!.innerHTML = '';
      } else {
        // Just got enabled - re-render
        this.render();
      }
    }
    if (name === 'show-debug' && oldValue !== newValue) {
      this.render();
      if (this.hasAttribute('show')) this.initEmbedIfNeeded();
    }
    if (name === 'show' && oldValue !== newValue) {
      if (newValue !== null) this.initEmbedIfNeeded();
      this.dispatchEvent(new CustomEvent('as-inspector-toggled', {
        detail: { show: this.hasAttribute('show') },
        bubbles: true,
        composed: true
      }));
    }
  }

  get show(): boolean {
    return this.hasAttribute('show');
  }

  set show(value: boolean) {
    if (value) {
      this.setAttribute('show', '');
    } else {
      this.removeAttribute('show');
    }
  }
}

if (!customElements.get('as-inspector')) {
  customElements.define('as-inspector', AsInspector);
} else {
  console.warn('as-inspector already defined');
}
